/**
 * Converts an array of key-value tuples (for e.g ["key", "value"]), into a record.
 * (for eg. output -> { "key": "value" })
 * NOTE: This function will discard duplicate key occurrences and only keep the last occurrence. If you do not want that behaviour,
 * use `tupleWithSamesKeysToRecord`.
 * @param tuples Array of tuples ([key, value])
 * @returns A record with value corresponding to the last occurrence of that key
 */
export const tupleToRecord = <
  KeyType extends string | number | symbol,
  ValueType,
>(
  tuples: [KeyType, ValueType][]
): Record<KeyType, ValueType> =>
  // Every emitted property comes from a typed tuple; duplicate keys keep the last value.
  Object.fromEntries(tuples) as Record<KeyType, ValueType>

/**
 * Converts an array of key-value tuples (for e.g ["key", "value"]), into a record.
 * (for eg. output -> { "key": ["value"] })
 * NOTE: If you do not want the array as values (because of duplicate keys) and want to instead get the last occurrence, use `tupleToRecord`
 * @param tuples Array of tuples ([key, value])
 * @returns A Record with values being arrays corresponding to each key occurrence
 */
export const tupleWithSameKeysToRecord = <
  KeyType extends string | number | symbol,
  ValueType,
>(
  tuples: [KeyType, ValueType][]
): Record<KeyType, ValueType[]> => {
  // By the end of the function we do ensure this typing, this can't be inferred now though, hence the assertion
  const out = {} as Record<KeyType, ValueType[]>

  for (const [key, value] of tuples) {
    if (!out[key]) {
      out[key] = [value]
    } else {
      out[key].push(value)
    }
  }

  return out
}
